import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Button, 
  Container, 
  Grid, 
  Typography, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { Add, Edit, Delete, Person, Lock } from '@mui/icons-material';
import axios from 'axios';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { hosturl } from 'constants/constant';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
  },
}));

const StaffCreation = () => {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    email: '',
    username: '',
    password: '',
    role: 'admin',
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [open, setOpen] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [editingAdmin, setEditingAdmin] = useState(null);

  useEffect(() => {
    const fetchAdminDetails = async () => {
      setFetching(true);
      try {
        const response = await axios.get(`${hosturl}/admindetails`);
        setAdminList(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch admin details.');
      } finally {
        setFetching(false);
      }
    };

    fetchAdminDetails();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let response;
      if (editingAdmin) {
        // Update the admin
        response = await axios.put(`${hosturl}/adminupdate/${editingAdmin}`, formData);
      } else {
        // Create a new admin
        response = await axios.post(`${hosturl}/adminregister`, formData);
      }

      setSuccess(response.data.message);
      setLoading(false);
      setOpen(false);
      setEditingAdmin(null);
      setFormData({
        phoneNumber: '',
        email: '',
        username: '',
        password: '',
        role: 'admin',
      });

      const updatedAdminList = await axios.get(`${hosturl}/admindetails`);
      setAdminList(updatedAdminList.data);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Server error. Please try again later.');
    }
  };

  const handleEditClick = (admin) => {
    setFormData({
      username: admin.username,
      email: admin.email,
      phoneNumber: admin.phoneNumber,
      password: '',
      role: 'admin', // Fixed role
    });
    setEditingAdmin(admin._id);
    setOpen(true);
  };

  const handleDeleteClick = async (adminId) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        await axios.delete(`${hosturl}/admindelete/${adminId}`);
        const updatedAdminList = await axios.get(`${hosturl}/admindetails`);
        setAdminList(updatedAdminList.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete admin.');
      }
    }
  };

  const handleDialogClose = () => {
    setOpen(false);
    setError('');
    setSuccess('');
  };

  const handleAddNewClick = () => {
    setOpen(true);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Grid item>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                Admin Management
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Manage your admin accounts and permissions
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={handleAddNewClick}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: 'none',
                  color: '#fff'  
                }}
              >
                Add New Admin
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {fetching ? (
            <Grid container justifyContent="center" sx={{ py: 8 }}>
              <CircularProgress size={60} />
            </Grid>
          ) : (
            <>
              {adminList.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="textSecondary">
                    No admin accounts found. Create your first admin account.
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={3}>
                  {adminList.map((admin) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={admin._id}>
                      <StyledCard>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Avatar
                            sx={{
                              width: 80,
                              height: 80,
                              mx: 'auto',
                              mb: 2,
                              bgcolor: 'primary.main',
                            }}
                          >
                            <Person sx={{ fontSize: 30 }} />
                          </Avatar>
                          <Typography variant="h6" gutterBottom>
                            {admin.username}
                          </Typography>
                          <Chip
                            icon={<Lock />}
                            label="Admin"
                            color="primary"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" style={{fontSize:'15px'}} gutterBottom>
                            <strong>Email:</strong> {admin.email}
                          </Typography>
                          <Typography variant="body2" style={{fontSize:'15px'}}>
                            <strong>Phone:</strong> {admin.phoneNumber}
                          </Typography>
                          <Grid container justifyContent="center" spacing={1} sx={{ mt: 2 }}>
                            <Grid item>
                              <Tooltip title="Edit Admin">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleEditClick(admin)}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                            <Grid item>
                              <Tooltip title="Delete Admin">
                                <IconButton
                                  color="error"
                                  onClick={() => handleDeleteClick(admin._id)}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </Paper>

        {/* Add/Edit Admin Dialog */}
        <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
          <DialogTitle
            sx={{
              bgcolor: 'primary.main',
              color: '#fff',
              py: 2,
              px: 3,
              borderTopLeftRadius: 'inherit',
              borderTopRightRadius: 'inherit',
            }}
          >
            <Typography variant="h6" style={{color:'#fff'}}>
              {editingAdmin ? 'Edit Admin Account' : 'Create New Admin Account'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ py: 4 }}>
            {success && (
              <Typography color="success.main" sx={{ mb: 2 }}>
                {success}
              </Typography>
            )}
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Username"
                    variant="outlined"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    size="medium"
                    InputProps={{
                      style: { borderRadius: 8 },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    size="medium"
                    InputProps={{
                      style: { borderRadius: 8 },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    variant="outlined"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    size="medium"
                    InputProps={{
                      style: { borderRadius: 8 },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    variant="outlined"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingAdmin}
                    size="medium"
                    InputProps={{
                      style: { borderRadius: 8 },
                    }}
                    helperText={editingAdmin ? "Leave blank to keep current password" : ""}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Role"
                    variant="outlined"
                    name="role"
                    value="admin"
                    disabled
                    size="medium"
                    InputProps={{
                      style: { borderRadius: 8 },
                      readOnly: true,
                    }}
                  />
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={handleDialogClose}
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                mr: 2,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={loading}
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                color:'#fff'
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 
               (editingAdmin ? 'Update Account' : 'Create Account')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default StaffCreation;
